<script setup lang="ts">
import { isAxiosError } from 'axios'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
  ExternalEventDataDTO,
  ExternalGradeImportError,
  ExternalGradeImportResult,
  ExternalPdfExportSettings,
  ExternalParticipantDTO,
  ExternalSetAttendanceRequest,
  ExternalSetGradeRequest,
  ExternalXlsxExportProfile,
  ExternalXlsxExportSettings,
} from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import {
  externalAuth,
  downloadExternalGradeImport,
  exportExternalParticipantPdf,
  exportExternalParticipantXlsx,
  getExternalData,
  importExternalGradesXlsx,
  setExternalAttendance,
  setExternalGrades,
} from '@/services/attendanceGradeService'
import {
  AttendanceStatus,
  VALID_GRADES,
  defaultExternalPdfExportSettings,
  defaultExternalXlsxExportSettings,
} from '@/types'
import { toggleListValue } from '@/utils/checkboxList'

definePage({
  meta: {
    requiresAuth: false,
    hideBreadcrumb: true,
  },
})

const { t, d, n } = useI18n()
const toast = useAppToast()
const route = useRoute()

const token = computed(() => (route.params as { token: string }).token)

// localStorage key for persisting the JWT token
const storageKey = computed(() => `ext-jwt-${token.value}`)

// Auth state
const password = ref('')
const jwtToken = ref<string | null>(null)
const authError = ref(false)
const authenticating = ref(false)

// Data state
const eventData = ref<ExternalEventDataDTO | null>(null)
const loading = ref(false)
const showLoading = useDelayedLoading(loading)
const saving = ref(false)
const activeTab = ref(0)

// Attendance: selected date for roll-call view
const selectedDate = ref<string | null>(null)
const showSummary = ref(false)
const exportDialogVisible = ref(false)
const exportDownloading = ref(false)
const exportSettings = ref<ExternalPdfExportSettings>(createDefaultPdfExportSettings())
const xlsxExportDialogVisible = ref(false)
const xlsxExportDownloading = ref(false)
const xlsxExportSettings = ref<ExternalXlsxExportSettings>({
  ...defaultExternalXlsxExportSettings,
})
const xlsxImportDialogVisible = ref(false)
const xlsxImportFile = ref<File | null>(null)
const xlsxImporting = ref(false)
const xlsxImportResult = ref<ExternalGradeImportResult | null>(null)
const xlsxImportErrors = ref<ExternalGradeImportError[]>([])
const xlsxImportError = ref<string | null>(null)
const downloadingImportIds = ref<Set<string>>(new Set())

// Pending changes
const pendingAttendance = ref<Map<string, ExternalSetAttendanceRequest>>(new Map())
const pendingGrades = ref<Map<string, ExternalSetGradeRequest>>(new Map())
const newDate = ref<Date | null>(null)
const datePickerPopover = ref()

// Combine server dates with locally added dates
const allAttendanceDates = computed(() => {
  if (!eventData.value) return []
  const dateSet = new Set(eventData.value.attendanceDates)
  // Add dates from pending changes (locally added dates)
  for (const req of pendingAttendance.value.values()) {
    dateSet.add(req.date)
  }
  return Array.from(dateSet).sort()
})

// Date options for the Select dropdown (newest first)
const dateOptions = computed(() =>
  [...allAttendanceDates.value].reverse().map((date) => ({
    label: d(new Date(date + 'T00:00:00'), 'dateonly'),
    value: date,
  })),
)

const statusOptions = [
  {
    value: AttendanceStatus.Present,
    label: t('enums.attendanceStatus.present'),
    severity: 'success' as const,
    icon: 'i-lucide-check',
  },
  {
    value: AttendanceStatus.Absent,
    label: t('enums.attendanceStatus.absent'),
    severity: 'danger' as const,
    icon: 'i-lucide-x',
  },
  {
    value: AttendanceStatus.Excused,
    label: t('enums.attendanceStatus.excused'),
    severity: 'warn' as const,
    icon: 'i-lucide-info',
  },
]

function todayDateStr(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

const isTodayAdded = computed(() => allAttendanceDates.value.includes(todayDateStr()))

const hasChanges = computed(() => pendingAttendance.value.size > 0 || pendingGrades.value.size > 0)
const isSlotScopedToken = computed(() => eventData.value?.slotId != null)
const canConfigurePlannedChanges = computed(() => !isSlotScopedToken.value)
const showParticipantAssignedSlot = computed(() => !isSlotScopedToken.value)
const exportDisabled = computed(
  () => exportDownloading.value || exportSettings.value.states.length === 0,
)
const isDefaultXlsxProfile = computed(() => xlsxExportSettings.value.profile === 'default')

function getParticipantMeta(p: ExternalParticipantDTO): string[] {
  const details: string[] = []

  if (showParticipantAssignedSlot.value && p.assignedSlotName) {
    details.push(`${t('pages.registrations.edit.assignedSlotLabel')}: ${p.assignedSlotName}`)
  }

  if (p.matriculationId) {
    details.push(`${t('fields.user.matriculationId')}: ${p.matriculationId}`)
  }

  if (p.email) {
    details.push(p.email)
  }

  return details
}

// Auto-select latest date when dates change
watch(
  allAttendanceDates,
  (dates) => {
    if (dates.length > 0 && (!selectedDate.value || !dates.includes(selectedDate.value))) {
      selectedDate.value = dates[dates.length - 1]!
    }
  },
  { immediate: true },
)

const gradeOptions = computed(() => [
  { label: '—', value: null as string | null },
  ...VALID_GRADES.map((g) => ({
    label: g === 'pass' ? t('enums.grade.pass') : g === 'fail' ? t('enums.grade.fail') : g,
    value: g as string | null,
  })),
])

const exportLayoutOptions = computed(() => [
  { label: t('pages.events.export.layout.allInOne'), value: 'all-in-one' as const },
  { label: t('pages.events.export.layout.sheetPerSlot'), value: 'sheet-per-slot' as const },
  { label: t('pages.events.export.layout.filePerSlot'), value: 'file-per-slot' as const },
])

const xlsxProfileOptions = computed<{ label: string; value: ExternalXlsxExportProfile }[]>(() => [
  { label: t('pages.events.export.profile.default'), value: 'default' },
  { label: t('pages.events.export.profile.spzGeneric'), value: 'spz-generic' },
  { label: t('pages.events.export.profile.spzEnglish'), value: 'spz-english' },
  { label: t('pages.events.export.profile.spzSpanish'), value: 'spz-spanish' },
  { label: t('pages.events.export.profile.spzSpanishHueber'), value: 'spz-spanish-hueber' },
])

const exportStatusOptions = computed(() => [
  { label: t('enums.registrationStatus.accepted'), value: 'accepted' as const },
  { label: t('enums.registrationStatus.offer'), value: 'offer' as const },
  { label: t('enums.registrationStatus.waitlist'), value: 'waitlist' as const },
  { label: t('enums.registrationStatus.open'), value: 'open' as const },
  { label: t('enums.registrationStatus.declined'), value: 'declined' as const },
])

function createDefaultPdfExportSettings(): ExternalPdfExportSettings {
  return {
    ...defaultExternalPdfExportSettings,
    includeMatriculationId: eventData.value?.defaultIncludeMatriculationId ?? true,
    includeEmail: eventData.value?.defaultIncludeEmail ?? true,
    states: [...defaultExternalPdfExportSettings.states],
  }
}

function resetExportSettings() {
  exportSettings.value = createDefaultPdfExportSettings()
}

function resetXlsxExportSettings() {
  const profile =
    eventData.value?.eventLanguage === 'spanish'
      ? 'spz-spanish'
      : eventData.value?.eventLanguage === 'english'
        ? 'spz-english'
        : eventData.value?.eventLanguage && eventData.value.eventLanguage !== 'unknown'
          ? 'spz-generic'
          : defaultExternalXlsxExportSettings.profile

  xlsxExportSettings.value = {
    ...defaultExternalXlsxExportSettings,
    profile,
    layout: 'all-in-one',
    includePlannedChanges: false,
  }
}

function clearExternalSession() {
  jwtToken.value = null
  eventData.value = null
  exportDialogVisible.value = false
  xlsxExportDialogVisible.value = false
  xlsxImportDialogVisible.value = false
  try {
    localStorage.removeItem(storageKey.value)
  } catch {
    // ignore
  }
}

async function authenticate() {
  authenticating.value = true
  authError.value = false
  try {
    const result = await externalAuth(token.value, password.value)
    jwtToken.value = result.token
    try {
      localStorage.setItem(storageKey.value, result.token)
    } catch {
      // localStorage may be unavailable (private browsing)
    }
    await loadData()
  } catch {
    authError.value = true
  } finally {
    authenticating.value = false
  }
}

async function loadData() {
  if (!jwtToken.value) return
  loading.value = true
  try {
    eventData.value = await getExternalData(token.value, jwtToken.value)
    pendingAttendance.value.clear()
    pendingGrades.value.clear()
    resetExportSettings()
  } catch {
    // Token may be expired — clear stored token and show login
    clearExternalSession()
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.external.loadError'),
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

// Restore JWT from localStorage on mount
onMounted(async () => {
  try {
    const stored = localStorage.getItem(storageKey.value)
    if (stored) {
      jwtToken.value = stored
      await loadData()
    }
  } catch {
    // localStorage unavailable
  }
})

watch(
  isSlotScopedToken,
  (slotScoped) => {
    if (slotScoped) {
      exportSettings.value.includePlannedChanges = false
    }
  },
  { immediate: true },
)

watch(
  () => xlsxExportSettings.value.profile,
  () => {
    if (!isDefaultXlsxProfile.value) {
      xlsxExportSettings.value.includeKITCardNumber = false
      xlsxExportSettings.value.includeKITAccount = false
    }
  },
)

// Attendance helpers
function getAttendanceKey(registrationId: number, appUserId: string, date: string): string {
  return `${registrationId}-${appUserId}-${date}`
}

function getAttendanceStatus(
  participant: ExternalParticipantDTO,
  date: string,
): AttendanceStatus | null {
  const key = getAttendanceKey(participant.registrationId, participant.appUserId, date)
  const pending = pendingAttendance.value.get(key)
  if (pending) {
    return pending.clear ? null : pending.status
  }
  const existing = participant.attendance.find((a) => a.date === date)
  return existing ? existing.status : null
}

function getStatusLabel(status: AttendanceStatus | null): string {
  if (status === null) return '—'
  if (status === AttendanceStatus.Present) return t('enums.attendanceStatus.present')
  if (status === AttendanceStatus.Absent) return t('enums.attendanceStatus.absent')
  return t('enums.attendanceStatus.excused')
}

function getStatusSeverity(status: AttendanceStatus | null): string {
  if (status === null) return 'secondary'
  if (status === AttendanceStatus.Present) return 'success'
  if (status === AttendanceStatus.Absent) return 'danger'
  return 'warn'
}

// Grade helpers
function getGradeKey(registrationId: number, appUserId: string): string {
  return `${registrationId}-${appUserId}`
}

function getCurrentGrade(participant: ExternalParticipantDTO): string | null {
  const key = getGradeKey(participant.registrationId, participant.appUserId)
  const pending = pendingGrades.value.get(key)
  return pending ? (pending.finalGrade ?? null) : (participant.finalGrade ?? null)
}

function getCurrentCredits(participant: ExternalParticipantDTO): number | null {
  const key = getGradeKey(participant.registrationId, participant.appUserId)
  const pending = pendingGrades.value.get(key)
  return pending ? (pending.finalGradeCredits ?? null) : (participant.finalGradeCredits ?? null)
}

/**
 * Grade and credits are saved together, so the pending entry always carries both. It is dropped
 * again when the edit brought the participant back to their stored values.
 */
function applyPendingGrade(
  participant: ExternalParticipantDTO,
  finalGrade: string | null,
  finalGradeCredits: number | null,
) {
  const key = getGradeKey(participant.registrationId, participant.appUserId)
  if (
    finalGrade === (participant.finalGrade ?? null) &&
    finalGradeCredits === (participant.finalGradeCredits ?? null)
  ) {
    pendingGrades.value.delete(key)
  } else {
    pendingGrades.value.set(key, {
      registrationId: participant.registrationId,
      appUserId: participant.appUserId,
      finalGrade,
      finalGradeCredits,
    })
  }
}

function setGrade(participant: ExternalParticipantDTO, value: string | null) {
  // Clearing the grade clears the credits it earned — mirror what the backend will do.
  applyPendingGrade(participant, value, value === null ? null : getCurrentCredits(participant))
}

function setCredits(participant: ExternalParticipantDTO, value: number | null) {
  applyPendingGrade(participant, getCurrentGrade(participant), value ?? null)
}

function addDate(date?: Date) {
  const nd = date ?? newDate.value
  if (!nd || !eventData.value) return
  const dateStr = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`
  if (!eventData.value.attendanceDates.includes(dateStr)) {
    eventData.value.attendanceDates.push(dateStr)
    eventData.value.attendanceDates.sort()
  }
  selectedDate.value = dateStr
  newDate.value = null
}

// Auto-add date when DatePicker value changes
watch(newDate, (val) => {
  if (val) addDate(val)
})

function addToday() {
  if (!eventData.value) return
  const dateStr = todayDateStr()
  if (!eventData.value.attendanceDates.includes(dateStr)) {
    eventData.value.attendanceDates.push(dateStr)
    eventData.value.attendanceDates.sort()
  }
  selectedDate.value = dateStr
}

function setAttendanceStatus(
  participant: ExternalParticipantDTO,
  date: string,
  status: AttendanceStatus,
) {
  const key = getAttendanceKey(participant.registrationId, participant.appUserId, date)
  const current = getAttendanceStatus(participant, date)
  const hasExisting = participant.attendance.some((a) => a.date === date)
  if (current === status) {
    if (hasExisting) {
      pendingAttendance.value.set(key, {
        registrationId: participant.registrationId,
        appUserId: participant.appUserId,
        date,
        status: AttendanceStatus.Present,
        clear: true,
      })
    } else {
      pendingAttendance.value.delete(key)
    }
  } else {
    pendingAttendance.value.set(key, {
      registrationId: participant.registrationId,
      appUserId: participant.appUserId,
      date,
      status,
    })
  }
}

interface ExternalAttendanceSummary {
  present: number
  absent: number
  excused: number
  total: number
  percentage: number
  severity: 'danger' | 'success'
}

function getExternalAttendanceSummary(
  participant: ExternalParticipantDTO,
): ExternalAttendanceSummary {
  let present = 0
  let absent = 0
  let excused = 0
  for (const date of allAttendanceDates.value) {
    const status = getAttendanceStatus(participant, date)
    if (status === AttendanceStatus.Present) present++
    else if (status === AttendanceStatus.Absent) absent++
    else if (status === AttendanceStatus.Excused) excused++
  }
  const total = allAttendanceDates.value.length
  const recorded = present + absent + excused
  const percentage = recorded > 0 ? Math.round((present / recorded) * 100) : 0
  const severity: 'danger' | 'success' = percentage < 80 ? 'danger' : 'success'
  return { present, absent, excused, total, percentage, severity }
}

function statusActiveClass(status: AttendanceStatus): string {
  if (status === AttendanceStatus.Present)
    return 'border-green-500 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
  if (status === AttendanceStatus.Absent)
    return 'border-red-500 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
  return 'border-yellow-500 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
}

function getGradeSeverity(grade: string | null): string {
  if (!grade) return 'secondary'
  if (grade === 'fail' || grade === '5.0') return 'danger'
  if (grade === 'pass') return 'success'
  const num = parseFloat(grade)
  if (num <= 2.0) return 'success'
  if (num <= 3.0) return 'info'
  return 'warn'
}

async function saveChanges() {
  if (!jwtToken.value || !hasChanges.value) return
  saving.value = true
  try {
    if (pendingAttendance.value.size > 0) {
      await setExternalAttendance(
        token.value,
        jwtToken.value,
        Array.from(pendingAttendance.value.values()),
      )
    }
    if (pendingGrades.value.size > 0) {
      await setExternalGrades(token.value, jwtToken.value, Array.from(pendingGrades.value.values()))
    }
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.external.saveSuccess'),
      duration: 3000,
    })
    await loadData()
  } catch {
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.external.saveError'),
      duration: 5000,
    })
  } finally {
    saving.value = false
  }
}

function openExportDialog() {
  resetExportSettings()
  exportDialogVisible.value = true
}

async function downloadParticipantPdf() {
  if (!jwtToken.value) return

  if (exportSettings.value.states.length === 0) {
    toast.add({
      color: 'warning',
      title: t('pages.events.export.errors.noStates'),
      duration: 4000,
    })
    return
  }

  exportDownloading.value = true

  try {
    await exportExternalParticipantPdf(token.value, jwtToken.value, exportSettings.value)
    exportDialogVisible.value = false
  } catch (error) {
    if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
      clearExternalSession()
      toast.add({
        color: 'error',
        title: t('common.messages.error'),
        description: t('features.external.export.authExpired'),
        duration: 5000,
      })
      return
    }

    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.external.export.downloadError'),
      duration: 5000,
    })
  } finally {
    exportDownloading.value = false
  }
}

function openXlsxExportDialog() {
  resetXlsxExportSettings()
  xlsxExportDialogVisible.value = true
}

async function downloadParticipantXlsx() {
  if (!jwtToken.value) return

  xlsxExportDownloading.value = true
  try {
    await exportExternalParticipantXlsx(token.value, jwtToken.value, xlsxExportSettings.value)
    xlsxExportDialogVisible.value = false
  } catch (error) {
    if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
      clearExternalSession()
      toast.add({
        color: 'error',
        title: t('common.messages.error'),
        description: t('features.external.export.authExpired'),
        duration: 5000,
      })
      return
    }

    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.external.xlsxExport.downloadError'),
      duration: 5000,
    })
  } finally {
    xlsxExportDownloading.value = false
  }
}

function openXlsxImportDialog() {
  xlsxImportFile.value = null
  xlsxImportResult.value = null
  xlsxImportErrors.value = []
  xlsxImportError.value = null
  xlsxImportDialogVisible.value = true
}

function onXlsxImportFileSelect(event: { files: File[] }) {
  xlsxImportFile.value = event.files[0] ?? null
  xlsxImportResult.value = null
  xlsxImportErrors.value = []
  xlsxImportError.value = null
}

function clearXlsxImportFile() {
  xlsxImportFile.value = null
  xlsxImportResult.value = null
  xlsxImportErrors.value = []
  xlsxImportError.value = null
}

function getXlsxImportRowError(error: ExternalGradeImportError): string {
  // Quote the rejected cell back when the backend told us what it was — otherwise the row
  // only says the grade was unsupported, without saying which one.
  if (error.code === 'invalidGrade' && error.value) {
    return t('features.external.xlsxImport.errors.invalidGradeValue', { value: error.value })
  }

  if (error.code === 'invalidCredits' && error.value) {
    return t('features.external.xlsxImport.errors.invalidCreditsValue', { value: error.value })
  }

  const knownCodes = new Set([
    'missingIdentifier',
    'duplicateIdentifier',
    'invalidGrade',
    'invalidCredits',
    'noMatch',
    'ambiguousMatch',
    'duplicateParticipant',
  ])
  return knownCodes.has(error.code)
    ? t(`features.external.xlsxImport.errors.${error.code}`)
    : error.message
}

function getXlsxImportRequestError(code: string | undefined, fallback: string | undefined): string {
  const knownCodes = new Set([
    'emptyFile',
    'fileTooLarge',
    'unsupportedFile',
    'invalidWorkbook',
    'invalidRows',
  ])
  return code && knownCodes.has(code)
    ? t(`features.external.xlsxImport.requestErrors.${code}`)
    : (fallback ?? t('features.external.xlsxImport.importError'))
}

async function importParticipantGrades() {
  if (!xlsxImportFile.value) {
    xlsxImportError.value = t('features.external.xlsxImport.noFile')
    return
  }

  if (!hasChanges.value) {
    void performXlsxImport()
    return
  }

  const accepted = await appConfirm({
    message: t('features.external.xlsxImport.unsavedMessage'),
    title: t('features.external.xlsxImport.unsavedHeader'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.discard'),
    confirmColor: 'error',
  })
  if (accepted) await performXlsxImport()
}

async function performXlsxImport() {
  if (!jwtToken.value || !xlsxImportFile.value) return

  xlsxImporting.value = true
  xlsxImportResult.value = null
  xlsxImportErrors.value = []
  xlsxImportError.value = null

  try {
    xlsxImportResult.value = await importExternalGradesXlsx(
      token.value,
      jwtToken.value,
      xlsxImportFile.value,
    )
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.external.xlsxImport.success', {
        updated: xlsxImportResult.value.updated,
      }),
      duration: 4000,
    })
    await loadData()
  } catch (error) {
    if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
      clearExternalSession()
      toast.add({
        color: 'error',
        title: t('common.messages.error'),
        description: t('features.external.export.authExpired'),
        duration: 5000,
      })
      return
    }

    if (isAxiosError(error)) {
      const data = error.response?.data as
        | { detail?: string; error?: string; code?: string; errors?: ExternalGradeImportError[] }
        | undefined
      xlsxImportError.value = getXlsxImportRequestError(data?.code, data?.detail ?? data?.error)
      xlsxImportErrors.value = data?.errors ?? []
    } else {
      xlsxImportError.value = t('features.external.xlsxImport.importError')
    }
  } finally {
    xlsxImporting.value = false
  }
}

async function downloadImportedFile(fileId: string) {
  if (!jwtToken.value) return

  downloadingImportIds.value.add(fileId)
  try {
    await downloadExternalGradeImport(token.value, jwtToken.value, fileId)
  } catch (error) {
    if (isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0)) {
      clearExternalSession()
      toast.add({
        color: 'error',
        title: t('common.messages.error'),
        description: t('features.external.export.authExpired'),
        duration: 5000,
      })
      return
    }

    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.external.xlsxImport.history.downloadError'),
      duration: 5000,
    })
  } finally {
    downloadingImportIds.value.delete(fileId)
  }
}
</script>

<template>
  <div class="px-2 py-3 sm:px-4">
    <!-- Auth form -->
    <div v-if="!jwtToken" class="flex items-center justify-center py-16">
      <div class="mycard w-full max-w-md p-6">
        <h2 class="mb-4 text-center text-2xl font-semibold">
          {{ t('features.external.title') }}
        </h2>
        <p class="mb-6 text-center text-muted">
          {{ t('features.external.enterPassword') }}
        </p>
        <form @submit.prevent="authenticate">
          <div class="mb-4">
            <label for="ext-pw" class="mb-2 block font-medium">
              {{ t('features.external.password') }}
            </label>
            <AppPassword
              id="ext-pw"
              v-model="password"
              :feedback="false"
              toggle-mask
              class="w-full"
              input-class="w-full"
              @keyup.enter="authenticate"
            />
          </div>
          <AppAlert v-if="authError" color="error" class="mb-4" :closable="false">
            {{ t('features.external.authError') }}
          </AppAlert>
          <UButton
            type="submit"
            :label="t('features.external.login')"
            :loading="authenticating"
            class="w-full"
          />
        </form>
      </div>
    </div>

    <!-- Data view -->
    <div v-else-if="showLoading" class="py-8 text-center">
      <AppSpinner style="width: 3rem; height: 3rem" />
    </div>

    <div v-else-if="eventData">
      <!-- Sticky header with save bar -->
      <div
        class="sticky top-0 z-10 -mx-2 mb-3 border-b border-neutral-200 bg-white px-2 pb-2 sm:-mx-4 sm:px-4 dark:border-neutral-700 dark:bg-neutral-900"
      >
        <div class="flex items-center justify-between gap-2 pt-2">
          <div class="min-w-0">
            <h2 class="m-0 truncate text-lg font-semibold sm:text-2xl">
              {{ eventData.eventTitle }}
            </h2>
            <p v-if="eventData.slotName" class="m-0 truncate text-sm text-muted">
              {{ eventData.slotName }}
            </p>
          </div>
          <div class="flex shrink-0 gap-2">
            <UButton
              :label="t('features.external.export.openDialog')"
              icon="i-lucide-file-text"
              color="neutral"
              size="sm"
              @click="openExportDialog"
              variant="outline"
            />
            <UButton
              v-if="hasChanges"
              icon="i-lucide-undo"
              color="neutral"
              size="sm"
              v-app-tooltip.bottom="t('common.actions.reset')"
              @click="loadData"
              variant="outline"
            />
            <UButton
              :label="t('common.actions.save')"
              icon="i-lucide-save"
              :disabled="!hasChanges"
              :loading="saving"
              size="sm"
              @click="saveChanges"
            />
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <AppTabs v-model:value="activeTab">
        <AppTabList>
          <AppTab v-if="eventData.attendanceEnabled" :value="0">
            <UIcon name="i-lucide-square-check" class="mr-1" />
            {{ t('features.external.attendance') }}
          </AppTab>
          <AppTab v-if="eventData.gradesEnabled" :value="eventData.attendanceEnabled ? 1 : 0">
            <UIcon name="i-lucide-star" class="mr-1" />
            {{ t('features.external.grades') }}
          </AppTab>
        </AppTabList>

        <AppTabPanels>
          <!-- Attendance panel — Roll Call view -->
          <AppTabPanel v-if="eventData.attendanceEnabled" :value="0">
            <div v-if="eventData.participants.length === 0" class="py-8 text-center">
              <p class="text-muted">
                {{ t('features.external.noParticipants') }}
              </p>
            </div>
            <template v-else>
              <!-- Add date controls -->
              <div class="mb-3 flex items-center gap-2">
                <AppPopover ref="datePickerPopover">
                  <AppDatePicker
                    v-model="newDate"
                    inline
                    @date-select="datePickerPopover?.hide()"
                  />
                </AppPopover>
                <UButton
                  :label="t('features.attendance.addDate')"
                  icon="i-lucide-calendar-plus"
                  color="neutral"
                  class="flex-1"
                  @click="datePickerPopover?.toggle($event)"
                  variant="outline"
                />
                <UButton
                  :label="t('features.attendance.addToday')"
                  icon="i-lucide-calendar"
                  :disabled="isTodayAdded"
                  color="neutral"
                  @click="addToday"
                  variant="outline"
                />
              </div>

              <div v-if="allAttendanceDates.length === 0" class="py-8 text-center">
                <p class="text-muted">
                  {{ t('features.external.noDates') }}
                </p>
              </div>

              <template v-else>
                <!-- Date selector -->
                <div class="mb-4 flex items-center gap-2">
                  <UIcon name="i-lucide-calendar" class="text-muted" />
                  <AppSelect
                    v-model="selectedDate"
                    :options="dateOptions"
                    option-label="label"
                    option-value="value"
                    :placeholder="t('features.external.selectDate')"
                    class="flex-1"
                  />
                </div>

                <!-- Roll Call: vertical participant list for selected date -->
                <div v-if="selectedDate" class="space-y-2">
                  <div
                    v-for="p in eventData.participants"
                    :key="p.registrationId"
                    class="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2.5 dark:border-neutral-700"
                  >
                    <!-- Name + summary -->
                    <div class="min-w-0 flex-1">
                      <div class="truncate text-sm leading-tight font-medium">
                        {{ p.userName ?? t('common.terms.unknown') }}
                      </div>
                      <div
                        v-for="detail in getParticipantMeta(p)"
                        :key="detail"
                        class="mt-1 truncate text-xs text-muted"
                      >
                        {{ detail }}
                      </div>
                      <UBadge
                        v-if="getExternalAttendanceSummary(p).total > 0"
                        :label="`${getExternalAttendanceSummary(p).percentage}%`"
                        :color="toAppColor(getExternalAttendanceSummary(p).severity)"
                        class="mt-1 text-xs"
                        v-app-tooltip.bottom="
                          `${t('enums.attendanceStatus.present')}: ${getExternalAttendanceSummary(p).present} · ${t('enums.attendanceStatus.absent')}: ${getExternalAttendanceSummary(p).absent} · ${t('enums.attendanceStatus.excused')}: ${getExternalAttendanceSummary(p).excused}`
                        "
                      />
                    </div>
                    <!-- Status buttons -->
                    <div class="flex shrink-0 gap-2">
                      <button
                        v-for="opt in statusOptions"
                        :key="opt.value"
                        class="flex h-11 w-11 items-center justify-center rounded-full border-2 text-lg transition-colors active:scale-95"
                        :class="
                          getAttendanceStatus(p, selectedDate) === opt.value
                            ? statusActiveClass(opt.value)
                            : 'border-neutral-300 text-dimmed hover:bg-neutral-100 dark:border-neutral-600 dark:text-neutral-500 dark:hover:bg-neutral-800'
                        "
                        :title="opt.label"
                        @click="setAttendanceStatus(p, selectedDate, opt.value)"
                      >
                        <UIcon :name="opt.icon" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Summary toggle -->
                <div class="mt-4">
                  <UButton
                    :label="
                      showSummary
                        ? t('features.external.hideSummary')
                        : t('features.external.showSummary')
                    "
                    :icon="showSummary ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                    color="neutral"
                    size="sm"
                    @click="showSummary = !showSummary"
                    variant="ghost"
                  />
                  <div v-if="showSummary" class="mt-2">
                    <table class="w-full border-collapse text-sm">
                      <thead>
                        <tr class="border-b border-neutral-200 dark:border-neutral-700">
                          <th class="px-2 py-2 text-left font-semibold">
                            {{ t('features.external.participant') }}
                          </th>
                          <th
                            v-for="date in allAttendanceDates"
                            :key="date"
                            class="px-1 py-2 text-center font-semibold whitespace-nowrap"
                          >
                            {{ d(new Date(date + 'T00:00:00'), 'short') }}
                          </th>
                          <th class="px-2 py-2 text-center font-semibold">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="p in eventData.participants"
                          :key="p.registrationId"
                          class="border-b border-neutral-100 dark:border-neutral-800"
                        >
                          <td class="px-2 py-1.5">
                            <div class="font-medium">
                              {{ p.userName ?? t('common.terms.unknown') }}
                            </div>
                            <div
                              v-for="detail in getParticipantMeta(p)"
                              :key="detail"
                              class="mt-0.5 text-xs text-muted"
                            >
                              {{ detail }}
                            </div>
                          </td>
                          <td
                            v-for="date in allAttendanceDates"
                            :key="date"
                            class="px-1 py-1.5 text-center"
                          >
                            <UBadge
                              :label="getStatusLabel(getAttendanceStatus(p, date))"
                              :color="toAppColor(getStatusSeverity(getAttendanceStatus(p, date)))"
                              class="text-xs"
                            />
                          </td>
                          <td class="px-2 py-1.5 text-center">
                            <UBadge
                              v-if="getExternalAttendanceSummary(p).total > 0"
                              :label="`${getExternalAttendanceSummary(p).percentage}%`"
                              :color="toAppColor(getExternalAttendanceSummary(p).severity)"
                            />
                            <span v-else class="text-dimmed">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </template>
          </AppTabPanel>

          <!-- Grades panel -->
          <AppTabPanel v-if="eventData.gradesEnabled" :value="eventData.attendanceEnabled ? 1 : 0">
            <div v-if="eventData.participants.length === 0" class="py-8 text-center">
              <p class="text-muted">
                {{ t('features.external.noParticipants') }}
              </p>
            </div>
            <div v-else>
              <div class="mb-3 flex flex-wrap justify-end gap-2">
                <UButton
                  :label="t('features.external.xlsxExport.openDialog')"
                  icon="i-lucide-file-spreadsheet"
                  color="neutral"
                  size="sm"
                  @click="openXlsxExportDialog"
                  variant="outline"
                />
                <UButton
                  :label="t('features.external.xlsxImport.openDialog')"
                  icon="i-lucide-upload"
                  color="neutral"
                  size="sm"
                  @click="openXlsxImportDialog"
                  variant="outline"
                />
              </div>
              <div
                v-for="p in eventData.participants"
                :key="p.registrationId"
                class="flex items-center gap-3 border-b border-neutral-100 px-1 py-3 dark:border-neutral-800"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">
                    {{ p.userName ?? t('common.terms.unknown') }}
                  </div>
                  <div
                    v-for="detail in getParticipantMeta(p)"
                    :key="detail"
                    class="mt-0.5 truncate text-xs text-muted"
                  >
                    {{ detail }}
                  </div>
                  <div v-if="p.finalGrade" class="mt-0.5 flex items-center gap-2">
                    <UBadge
                      :label="
                        p.finalGrade === 'pass'
                          ? t('enums.grade.pass')
                          : p.finalGrade === 'fail'
                            ? t('enums.grade.fail')
                            : p.finalGrade
                      "
                      :color="toAppColor(getGradeSeverity(p.finalGrade))"
                      class="text-xs"
                    />
                    <span class="text-xs text-muted">
                      {{
                        p.finalGradeCredits != null
                          ? t('features.grades.creditsValue', { value: n(p.finalGradeCredits) })
                          : t('features.grades.creditsDefault')
                      }}
                    </span>
                    <span v-if="p.gradedAt" class="text-xs text-muted">
                      {{ d(new Date(p.gradedAt), 'datetime') }}
                    </span>
                  </div>
                </div>
                <AppSelect
                  :model-value="getCurrentGrade(p)"
                  :options="gradeOptions"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('features.grades.selectGrade')"
                  class="w-36 shrink-0 sm:w-44"
                  @update:model-value="(val: string | null) => setGrade(p, val)"
                />
                <UInputNumber
                  :model-value="getCurrentCredits(p)"
                  :min="0"
                  :max="100"
                  :step="0.5"
                  :disabled="!getCurrentGrade(p)"
                  :placeholder="t('features.grades.creditsDefault')"
                  :aria-label="t('features.grades.credits')"
                  class="w-28 shrink-0 sm:w-32"
                  @update:model-value="(val: number | null) => setCredits(p, val)"
                />
              </div>
            </div>
          </AppTabPanel>
        </AppTabPanels>
      </AppTabs>

      <AppModal
        v-model:open="xlsxExportDialogVisible"
        :title="t('features.external.xlsxExport.dialogTitle')"
        modal
        :style="{ width: '42rem', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <form class="flex flex-col gap-5" @submit.prevent="downloadParticipantXlsx">
          <p class="text-sm text-muted">
            {{ t('features.external.xlsxExport.dialogSubtitle') }}
          </p>

          <div class="grid grid-cols-1 gap-5">
            <div class="flex flex-col gap-2">
              <label for="external-xlsx-profile" class="font-medium">
                {{ t('pages.events.export.profile.label') }}
              </label>
              <AppSelect
                id="external-xlsx-profile"
                v-model="xlsxExportSettings.profile"
                :options="xlsxProfileOptions"
                option-label="label"
                option-value="value"
                class="w-full"
              />
            </div>
          </div>

          <template v-if="isDefaultXlsxProfile">
            <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="flex items-start gap-3">
                <UCheckbox
                  v-model="xlsxExportSettings.includeKITCardNumber"
                  id="externalXlsxIncludeKITCardNumber"
                  class="mt-0.5"
                />
                <div>
                  <label for="externalXlsxIncludeKITCardNumber" class="cursor-pointer font-medium">
                    {{ t('pages.events.export.options.includeKITCardNumber') }}
                  </label>
                  <p class="mt-1 text-sm text-muted">
                    {{ t('pages.events.export.options.includeKITCardNumberHint') }}
                  </p>
                </div>
              </div>
            </div>

            <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="flex items-start gap-3">
                <UCheckbox
                  v-model="xlsxExportSettings.includeKITAccount"
                  id="externalXlsxIncludeKITAccount"
                  class="mt-0.5"
                />
                <div>
                  <label for="externalXlsxIncludeKITAccount" class="cursor-pointer font-medium">
                    {{ t('pages.events.export.options.includeKITAccount') }}
                  </label>
                  <p class="mt-1 text-sm text-muted">
                    {{ t('pages.events.export.options.includeKITAccountHint') }}
                  </p>
                </div>
              </div>
            </div>
          </template>

          <div class="flex justify-end gap-2">
            <UButton
              type="button"
              :label="t('common.actions.cancel')"
              color="neutral"
              @click="xlsxExportDialogVisible = false"
              variant="ghost"
            />
            <UButton
              type="submit"
              :label="t('features.external.xlsxExport.download')"
              icon="i-lucide-download"
              :loading="xlsxExportDownloading"
            />
          </div>
        </form>
      </AppModal>

      <AppModal
        v-model:open="xlsxImportDialogVisible"
        :title="t('features.external.xlsxImport.dialogTitle')"
        modal
        :style="{ width: '44rem', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <form class="flex flex-col gap-5" @submit.prevent="importParticipantGrades">
          <p class="text-sm text-muted">
            {{ t('features.external.xlsxImport.dialogSubtitle') }}
          </p>

          <div class="flex flex-col gap-2">
            <span id="external-xlsx-file-label" class="font-medium">
              {{ t('features.external.xlsxImport.fileLabel') }}
            </span>
            <div
              v-if="!xlsxImportFile"
              class="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-neutral-300 p-6 dark:border-neutral-600"
            >
              <UIcon
                name="i-lucide-file-spreadsheet"
                class="text-4xl text-green-700 dark:text-green-400"
                aria-hidden="true"
              />
              <AppFileUpload
                mode="basic"
                accept=".xlsx"
                :max-file-size="5242880"
                :auto="false"
                :choose-label="t('features.external.xlsxImport.chooseFile')"
                :choose-button-props="{
                  'aria-labelledby': 'external-xlsx-file-label',
                  'aria-describedby': 'external-xlsx-file-help',
                }"
                @select="onXlsxImportFileSelect"
              />
            </div>
            <div
              v-else
              class="flex items-center gap-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800"
            >
              <UIcon
                name="i-lucide-file-spreadsheet"
                class="text-2xl text-green-700 dark:text-green-400"
                aria-hidden="true"
              />
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{{ xlsxImportFile.name }}</div>
                <div class="text-xs text-muted">
                  {{ (xlsxImportFile.size / 1024).toFixed(1) }} KB
                </div>
              </div>
              <UButton
                type="button"
                icon="i-lucide-x"
                color="error"
                :aria-label="t('common.actions.remove')"
                @click="clearXlsxImportFile"
                variant="ghost"
              />
            </div>
            <small id="external-xlsx-file-help" class="text-muted">
              {{ t('features.external.xlsxImport.fileHelp') }}
            </small>
          </div>

          <AppAlert v-if="xlsxImportError" color="error" :closable="false" role="alert">
            {{ xlsxImportError }}
          </AppAlert>

          <div v-if="xlsxImportErrors.length" class="max-h-52 overflow-auto" aria-live="polite">
            <ul class="space-y-2 text-sm">
              <li
                v-for="error in xlsxImportErrors"
                :key="`${error.worksheet}-${error.row}-${error.identifier ?? ''}`"
                class="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
              >
                <strong
                  >{{ error.worksheet }}, {{ t('features.external.xlsxImport.row') }}
                  {{ error.row }}</strong
                >
                <span v-if="error.identifier"> · {{ error.identifier }}</span>
                <div>{{ getXlsxImportRowError(error) }}</div>
              </li>
            </ul>
          </div>

          <AppAlert v-if="xlsxImportResult" color="success" :closable="false" aria-live="polite">
            {{
              t('features.external.xlsxImport.summary', {
                examined: xlsxImportResult.examined,
                updated: xlsxImportResult.updated,
                unchanged: xlsxImportResult.unchanged,
                blankSkipped: xlsxImportResult.blankSkipped,
              })
            }}
          </AppAlert>

          <section class="flex flex-col gap-3" aria-live="polite">
            <div>
              <h3 class="m-0 text-base font-semibold">
                {{ t('features.external.xlsxImport.history.title') }}
              </h3>
              <p class="mt-1 mb-0 text-sm text-muted">
                {{ t('features.external.xlsxImport.history.description') }}
              </p>
            </div>

            <p
              v-if="!eventData?.importedFiles.length"
              class="m-0 rounded-lg bg-neutral-50 p-3 text-sm text-muted dark:bg-neutral-800 dark:text-neutral-400"
            >
              {{ t('features.external.xlsxImport.history.empty') }}
            </p>
            <ul v-else class="m-0 max-h-56 space-y-2 overflow-auto p-0">
              <li
                v-for="importedFile in eventData.importedFiles"
                :key="importedFile.id"
                class="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
              >
                <UIcon
                  name="i-lucide-file-spreadsheet"
                  class="text-xl text-green-700 dark:text-green-400"
                  aria-hidden="true"
                />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">{{ importedFile.fileName }}</div>
                  <div class="text-xs text-muted">
                    {{ d(new Date(importedFile.uploadedAt), 'datetime') }}
                  </div>
                </div>
                <UButton
                  type="button"
                  icon="i-lucide-download"
                  color="neutral"
                  :loading="downloadingImportIds.has(importedFile.id)"
                  :aria-label="
                    t('features.external.xlsxImport.history.download', {
                      fileName: importedFile.fileName,
                    })
                  "
                  @click="downloadImportedFile(importedFile.id)"
                  variant="ghost"
                />
              </li>
            </ul>
          </section>

          <div class="flex justify-end gap-2">
            <UButton
              type="button"
              :label="t('common.actions.cancel')"
              color="neutral"
              @click="xlsxImportDialogVisible = false"
              variant="ghost"
            />
            <UButton
              type="submit"
              :label="t('features.external.xlsxImport.import')"
              icon="i-lucide-upload"
              :loading="xlsxImporting"
            />
          </div>
        </form>
      </AppModal>

      <AppModal
        v-model:open="exportDialogVisible"
        :title="t('features.external.export.dialogTitle')"
        modal
        :style="{ width: '42rem', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <div class="flex flex-col gap-6">
          <p class="text-sm text-muted">
            {{ t('features.external.export.dialogSubtitle') }}
          </p>

          <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div class="flex flex-col gap-2">
              <label class="font-medium">{{ t('pages.events.export.layout.label') }}</label>
              <AppSelect
                v-model="exportSettings.layout"
                :options="exportLayoutOptions"
                option-label="label"
                option-value="value"
                class="w-full"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="font-medium">{{
                t('pages.events.export.options.presenceColumns')
              }}</label>
              <UInputNumber
                v-model="exportSettings.presenceColumns"
                :min="0"
                :max="14"
                class="w-full"
                :format-options="{ useGrouping: false }"
              />
              <p class="text-xs text-muted">
                {{ t('pages.events.export.options.presenceColumnsHint') }}
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <div>
              <h3 class="font-medium text-neutral-900 dark:text-white">
                {{ t('pages.events.export.states.label') }}
              </h3>
              <p class="text-sm text-muted">
                {{ t('pages.events.export.states.hint') }}
              </p>
            </div>

            <div class="flex flex-wrap gap-x-6 gap-y-4">
              <div
                v-for="status in exportStatusOptions"
                :key="status.value"
                class="min-w-34 rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700"
              >
                <div class="flex items-center gap-2">
                  <UCheckbox
                    :model-value="exportSettings.states.includes(status.value)"
                    @update:model-value="
                      exportSettings.states = toggleListValue(
                        exportSettings.states,
                        status.value,
                        $event,
                      )
                    "
                    :id="`export-status-${status.value}`"
                  />
                  <label :for="`export-status-${status.value}`" class="cursor-pointer font-medium">
                    {{ status.label }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="canConfigurePlannedChanges"
            class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
          >
            <div class="flex items-start gap-3">
              <UCheckbox
                v-model="exportSettings.includePlannedChanges"
                id="externalIncludePlannedChanges"
                class="mt-0.5"
              />
              <div>
                <label for="externalIncludePlannedChanges" class="cursor-pointer font-medium">
                  {{ t('pages.events.export.options.includePlannedChanges') }}
                </label>
                <p class="mt-1 text-sm text-muted">
                  {{ t('pages.events.export.options.includePlannedChangesHint') }}
                </p>
              </div>
            </div>
          </div>

          <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div class="flex items-start gap-3">
              <UCheckbox
                v-model="exportSettings.includeMatriculationId"
                id="externalIncludeMatriculationId"
                class="mt-0.5"
              />
              <div>
                <label for="externalIncludeMatriculationId" class="cursor-pointer font-medium">
                  {{ t('pages.events.export.options.includeMatriculationId') }}
                </label>
                <p class="mt-1 text-sm text-muted">
                  {{ t('pages.events.export.options.includeMatriculationIdHint') }}
                </p>
              </div>
            </div>
          </div>

          <div class="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div class="flex items-start gap-3">
              <UCheckbox
                v-model="exportSettings.includeEmail"
                id="externalIncludeEmail"
                class="mt-0.5"
              />
              <div>
                <label for="externalIncludeEmail" class="cursor-pointer font-medium">
                  {{ t('pages.events.export.options.includeEmail') }}
                </label>
                <p class="mt-1 text-sm text-muted">
                  {{ t('pages.events.export.options.includeEmailHint') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="flex justify-end gap-2">
            <UButton
              :label="t('common.actions.cancel')"
              color="neutral"
              @click="exportDialogVisible = false"
              variant="ghost"
            />
            <UButton
              :label="
                exportDownloading
                  ? t('features.external.export.downloading')
                  : t('features.external.export.download')
              "
              icon="i-lucide-download"
              :loading="exportDownloading"
              :disabled="exportDisabled"
              @click="downloadParticipantPdf"
            />
          </div>
        </template>
      </AppModal>
    </div>
  </div>
</template>
